import { useEffect, useRef, useState } from 'react';
import { Activity, Download, Trash2 } from 'lucide-react';
import { api } from '../lib/api.js';
import { Button } from '../components/ui/Button.js';

interface Sample {
  at: number;
  participants: number;
  publishers: number;
  activeAudio: number;
  estKbps: number;
  ok: boolean;
}

const POLL_MS = 1000;
const CHART_POINTS = 600; // Graph zeigt die letzten 10 Min (rollend)
const HARD_CAP = 36000; // CSV/Speicher: bis zu 10 h Aufzeichnung

export function MonitorPage(): JSX.Element {
  const [samples, setSamples] = useState<Sample[]>([]);
  const [running, setRunning] = useState(true);
  const [reachable, setReachable] = useState(true);
  const runningRef = useRef(running);
  runningRef.current = running;

  useEffect(() => {
    let alive = true;
    const tick = async (): Promise<void> => {
      if (!runningRef.current) return;
      try {
        const s = await api.get<Sample>('/api/v1/stats/live');
        if (!alive) return;
        setReachable(s.ok);
        setSamples((prev) => {
          const next = [...prev, s];
          return next.length > HARD_CAP ? next.slice(next.length - HARD_CAP) : next;
        });
      } catch {
        if (alive) setReachable(false);
      }
    };
    void tick();
    const t = window.setInterval(() => void tick(), POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, []);

  const last = samples[samples.length - 1];
  const chartSamples = samples.slice(-CHART_POINTS);
  const kbpsSeries = chartSamples.map((s) => s.estKbps);
  const maxKbps = Math.max(10, ...kbpsSeries);
  // Spitze/Ø über die GESAMTE Aufzeichnung (für die Dokumentation).
  const allKbps = samples.map((s) => s.estKbps);
  const avgKbps = allKbps.length
    ? Math.round(allKbps.reduce((a, b) => a + b, 0) / allKbps.length)
    : 0;
  const peakKbps = allKbps.length ? Math.max(...allKbps) : 0;

  function exportCsv(): void {
    const header = 'timestamp_iso,participants,publishers,active_audio,est_kbps\n';
    const rows = samples
      .map(
        (s) =>
          `${new Date(s.at).toISOString()},${s.participants},${s.publishers},${s.activeAudio},${s.estKbps}`,
      )
      .join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `livepuls-traffic-${new Date().toISOString().replace(/[:.]/g, '-')}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="p-6 space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-text flex items-center gap-2">
            <Activity size={20} className="text-accent" /> Server-Monitor
          </h1>
          <p className="text-sm text-text-dim mt-1">
            Live-Traffic & Aktivität für den Mehrgeräte-Test.{' '}
            {reachable ? (
              <span className="text-green-400">● LiveKit erreichbar</span>
            ) : (
              <span className="text-red-400">● LiveKit nicht erreichbar</span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant={running ? 'ghost' : 'primary'} size="sm" onClick={() => setRunning((r) => !r)}>
            {running ? 'Pause' : 'Weiter'}
          </Button>
          <Button variant="ghost" size="sm" leftIcon={<Download size={14} />} onClick={exportCsv}>
            CSV
          </Button>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<Trash2 size={14} />}
            onClick={() => setSamples([])}
          >
            Reset
          </Button>
        </div>
      </header>

      {/* Aktuelle Kennzahlen */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Geräte verbunden" value={last?.participants ?? 0} />
        <Stat label="Aktive Audio-Streams" value={last?.activeAudio ?? 0} />
        <Stat label="Durchsatz (geschätzt)" value={`${last?.estKbps ?? 0} kbit/s`} accent />
        <Stat label="Spitze / Ø" value={`${peakKbps} / ${avgKbps}`} sub="kbit/s" />
      </div>

      {/* Live-Graph */}
      <section className="card p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xxs uppercase tracking-wider text-text-mute">
            Durchsatz (kbit/s, geschätzt) · letzte {chartSamples.length}s · {samples.length}{' '}
            Messpunkte gesamt
          </div>
          <div className="text-xxs text-text-mute">max {maxKbps} kbit/s</div>
        </div>
        <Chart series={kbpsSeries} max={maxKbps} />
        <p className="text-xxs text-text-mute mt-2">
          Schätzung: aktive Audio-Streams × Teilnehmer × ~40 kbit/s (Opus). Reale Werte können je
          nach Routing abweichen; die Kurve zeigt die Last-Tendenz.
        </p>
      </section>
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string | number;
  sub?: string;
  accent?: boolean;
}): JSX.Element {
  return (
    <div className="card p-4">
      <div className="text-xxs uppercase tracking-wider text-text-mute">{label}</div>
      <div className={`mt-1 text-2xl font-bold tabular-nums ${accent ? 'text-accent' : 'text-text'}`}>
        {value}
      </div>
      {sub ? <div className="text-xxs text-text-mute">{sub}</div> : null}
    </div>
  );
}

function Chart({ series, max }: { series: number[]; max: number }): JSX.Element {
  const W = 1000;
  const H = 240;
  const n = series.length;
  if (n < 2) {
    return (
      <div className="h-[240px] flex items-center justify-center text-sm text-text-mute">
        Sammle Daten…
      </div>
    );
  }
  const dx = W / (CHART_POINTS - 1);
  const x0 = W - (n - 1) * dx; // rechtsbündig zeichnen
  const pts = series.map((v, i) => {
    const x = x0 + i * dx;
    const y = H - (v / max) * (H - 8) - 4;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const area = `M ${x0.toFixed(1)},${H} L ${pts.join(' L ')} L ${W},${H} Z`;
  const line = `M ${pts.join(' L ')}`;
  // 4 horizontale Hilfslinien.
  const grid = [0.25, 0.5, 0.75, 1].map((f) => H - f * (H - 8) - 4);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[240px]" preserveAspectRatio="none">
      {grid.map((y, i) => (
        <line key={i} x1={0} x2={W} y1={y} y2={y} stroke="#ffffff14" strokeWidth={1} />
      ))}
      <path d={area} fill="url(#g)" opacity={0.35} />
      <path d={line} fill="none" stroke="#00f2ff" strokeWidth={2} />
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#00f2ff" stopOpacity={0.5} />
          <stop offset="100%" stopColor="#00f2ff" stopOpacity={0} />
        </linearGradient>
      </defs>
    </svg>
  );
}
